import { CommandProvider } from "@/common/schema/commands/_common/command-descriptors";
import { editable, Editable } from "@/fx/data-helpers";
import assert from "assert";

export function section({ inactive, optional, disabled, readonly }: {
  inactive: boolean,
  optional: boolean,
  disabled: boolean,
  readonly: boolean
}) {
  assert(!inactive || !optional, 'When inactive is true, optional must be false');
  assert(!inactive || !disabled, 'When inactive is true, disabled must be false');
  assert(!inactive || !readonly, 'When inactive is true, readonly must be false');
  assert(!optional || !disabled, 'When optional is true, disabled must be false');
  assert(!optional || !readonly, 'When optional is true, readonly must be false');
  assert(!disabled || !readonly, 'When disabled is true, readonly must be false');
  return { inactive, optional, disabled, readonly };
}

type FieldDescriptor<TBase> = {
  // a?: any;
  // b?: any;
  // c: (_: TBase) => any;
};
type FormDescriptor<TBase, K extends keyof any> = {
  [P in K]: FieldDescriptor<TBase>;
};
export type FormData<TFormDescriptor> = {
  [K in keyof TFormDescriptor]: Editable;
};
export const formDefaults = <TFormDescriptor extends FormDescriptor<any, any>>(descriptor: TFormDescriptor): FormData<TFormDescriptor> =>
  Object.entries(descriptor).reduce((acc, [key, value]) => ({ ...acc, [key]: editable() }), {} as FormData<TFormDescriptor>);

export const formDataDescriptor = <TBase>() => <TFormDescriptor extends FormDescriptor<TBase, any>>(descriptor: TFormDescriptor) => descriptor;

export type Text = { type: "text", field: string };
export type Optional<T> = { type: "optional", field: T };

export const text = ({ field }: {
  field: string,
}): Text => {
  return { type: "text", field };
}
export const optional_text = ({ } = { })  => ({
});

export const FD = { text, optional_text };

export type UpdateSegment<TData, TCommand, TRawCommand, TResult, TContext extends { }> = {
  isDataChanged:   (_: TData) => boolean,
  commandProvider: (_: TData) => CommandProvider<TCommand, TRawCommand, TResult, TContext>,
}
export const updateSegment = <TCommand, TRawCommand, TResult, TContext extends { }, TData>({ isDataChanged, commandProvider }: {
  isDataChanged:   (_: TData) => boolean,
  commandProvider: (_: TData) => CommandProvider<TCommand, TRawCommand, TResult, TContext>,
}): UpdateSegment<TData, TCommand, TRawCommand, TResult, TContext> => {
  return { isDataChanged, commandProvider };
}

type HandlerProvider = <TCommand, TRawCommand, TResult>(name: string) => ({ command }: { command: TRawCommand }) => Promise<TResult>;

const executeCommands_ = (handler: HandlerProvider) => <TContext extends {}>(ctx?: TContext) => (providers: CommandProvider<any, any, any, TContext>[]) => {
  const executeCommand = <TCommand, TRawCommand, TResult>(provider: CommandProvider<TCommand, TRawCommand, TResult, TContext>) => {
    const command        = provider.command(ctx ?? { } as TContext);
    const commandHandler = handler(provider.descriptor.commandName);

    assert(commandHandler, `No handler found for command: ${provider.descriptor.commandName}`);

    return commandHandler({ command: provider.descriptor.commandToRaw(command) })
  }
  return Promise.all(
    providers.map(executeCommand)
  )
}
export const executeCommands = <TData, TContext extends {}>({ handler, updateSegments, formData, ctx }: {
  handler:        HandlerProvider,
  updateSegments: UpdateSegment<any, any, any, any, TContext>[],
  formData:       TData,
  ctx?:           TContext
}) => {
  const updateCommandProviders = updateSegments
    .filter(_ => _.isDataChanged  (formData))
    .map   (_ => _.commandProvider(formData));

  return executeCommands_(handler)(ctx)(updateCommandProviders);
}
